/**
 * Retail-options API client. One endpoint: `previewRetailOption` for
 * the "Add retail option" flow on the quote editor.
 *
 * Distinct from `distiListingsApi.ts` because there's no listings-
 * table involved here — the response feeds straight into a new
 * `QuoteOption`, no row created.
 */

import type {
  RetailOptionCommitRequest,
  RetailOptionCommitResponse,
  RetailOptionPreviewRequest,
  RetailOptionPreviewResponse,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

/**
 * POST /api/retail-options/preview
 *
 * Server-side fetch + parse of a retailer URL. Returns populated
 * quote-option fields (description / price / specs / image) ready to
 * drop into a new `QuoteOption`. Soft failures (anti-bot, parse miss)
 * come back as `status !== 'ok'` — caller still shows the dialog
 * with editable defaults so the tech can fill in what's missing.
 */
export async function previewRetailOption(
  body: RetailOptionPreviewRequest,
  signal?: AbortSignal,
): Promise<RetailOptionPreviewResponse> {
  const response = await fetch(`${API_BASE_URL}/retail-options/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Retail preview failed: ${response.status} ${text}`);
  }
  return response.json();
}

/**
 * POST /api/retail-options/commit
 *
 * Persist the user-confirmed retail option as a `cr001_disti_listings`
 * row under the appropriate per-retailer vendor (auto-creates the
 * vendor on first use). Returns the back-pointer values the caller
 * stamps onto the new QuoteOption (`sourceDistiId`/`sourceDistiSku`)
 * so the picker can re-find the listing on future quotes.
 */
export async function commitRetailOption(
  body: RetailOptionCommitRequest,
  signal?: AbortSignal,
): Promise<RetailOptionCommitResponse> {
  const response = await fetch(`${API_BASE_URL}/retail-options/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Retail commit failed: ${response.status} ${text}`);
  }
  return response.json();
}
